import { type ClientAuthenticatedUserWithSelectedRole } from '@/src/auth';
import { useLogger } from '@/src/hooks/internal/useLogger';
import { type UrqlClientError } from '@/src/integrations/urql';

import { useRootAuthenticatedUser } from './useRootAuthenticatedUser';

export class AuthenticatedUserNotFoundError extends Error {
  constructor() {
    super('A user was not found and no error was present.');
    this.name = 'AuthenticatedUserNotFoundError';
  }
}

export type UseAuthenticatedUserResponse =
  | {
      readonly error: AuthenticatedUserNotFoundError | UrqlClientError;
      readonly isFetching: false;
      /**
       * Whether the failure was a wholly unauthenticated request, mirrored from the wrapped query
       * hook so the provider can defer to the {@link AuthenticationRedirectExchange} redirect.
       */
      readonly isNotAuthenticated: boolean;
      readonly user?: never;
    }
  | {
      readonly error?: never;
      readonly isFetching: false;
      readonly isNotAuthenticated: false;
      readonly user: ClientAuthenticatedUserWithSelectedRole;
    }
  | {
      readonly error?: never;
      readonly isFetching: false;
      readonly isNotAuthenticated: false;
      readonly user?: never;
    }
  | {
      readonly error?: never;
      readonly isFetching: true;
      readonly isNotAuthenticated: false;
      readonly user: ClientAuthenticatedUserWithSelectedRole;
    }
  | {
      readonly error?: never;
      readonly isFetching: true;
      readonly isNotAuthenticated: false;
      readonly user?: never;
    };

/**
 * Returns the currently authenticated user, {@link ClientAuthenticatedUserWithSelectedRole}, along
 * with the error and/or loading status of the underlying urql query.
 *
 * It is important that this hook returns the previously resolved, stale user data, even when it
 * is refetching in the background (i.e. when 'isFetching' is true).  This can happen in various
 * scenarios, such as when the currently authenticated user's role is changed.  When events like
 * this occur, the stale user data should still be returned, rather than 'undefined', so that the
 * UI can continue to render based on the existing user data while the new user data is being
 * fetched in the background.
 *
 * This prevents the unmounting of the main authenticated component tree which has the following
 * effects:
 *
 * 1. The <CraftLoadingScreen /> is not needed and will not be rendered during these background
 *    refetches, which prevents flashes of the loading screen and provides for a better user
 *    experience.
 * 2. Any local state that is stored in the authenticated component tree will not be lost during
 *    these background refetches, which prevents UI disruptions and provides for a better user
 *    experience.
 * 3. Dialogs such as the {@link PostLoginDialog} will not flash content during the background
 *    refresh.
 *
 * When `pause` is true the underlying query is suspended and the hook reports a settled state with
 * no user - or the previously cached user, if one exists - and never synthesizes a not-found error.
 * This lets callers defer the request (e.g. until the browser is idle) without surfacing a spurious
 * error for the "no data yet" state that pausing produces.
 */
export const useAuthenticatedUser = (options?: {
  readonly pause: boolean;
}): UseAuthenticatedUserResponse => {
  const logger = useLogger();
  const isPaused = options?.pause ?? false;
  const { data, error, isFetching, isNotAuthenticated } = useRootAuthenticatedUser(
    {},
    { pause: isPaused },
  );

  if (isPaused) {
    return data
      ? { isFetching: false, isNotAuthenticated: false, user: data }
      : { isFetching: false, isNotAuthenticated: false };
  }

  if (error) {
    return { error, isFetching: false, isNotAuthenticated: isNotAuthenticated ?? false };
  }

  if (isFetching) {
    if (data) {
      return { isFetching: true, isNotAuthenticated: false, user: data };
    }
    return { isFetching: true, isNotAuthenticated: false };
  }

  if (!data) {
    const err = new AuthenticatedUserNotFoundError();
    logger.error(err);
    return { error: err, isFetching: false, isNotAuthenticated: false };
  }

  return { isFetching: false, isNotAuthenticated: false, user: data };
};
